/* eslint-disable */
import { RACE_CIRCUITS } from '@/constants/raceCircuits'
import { PlayerOwner } from '@/models/playerOwner'
import { Chocobo } from '@/models/chocobos'
import { Jockey } from '@/models/jockey'
import { Owner } from '@/models/owner'
import { useGameStore } from '@/store/game'
import { chocoboImages } from '@/utils/imageImports'
import { CHOCOBO_BASE_STATS } from '@/constants/chocoboConstants'

const STORAGE_OPTIMIZATIONS = {
  raceHistory: {
    compressRaceData: (race) => {
      // Safely handle potentially undefined race circuit
      if (!race || !race.circuit) {
        return {
          p: race?.position || 0,
          c: 'unknown',
          t: race?.raceType || 'unknown',
          f: race?.finishTime || 0,
          d: race?.day || 0,
          e: race?.earnings || 0
        }
      }

      return {
        p: race.position,
        c: race.circuit.id || 'unknown',
        t: race.raceType,
        f: race.finishTime,
        d: race.day,
        e: race.earnings || 0
      }
    },

    maxRacesPerChocobo: 20,
    
    decompressRaceData: (compressed) => {
      // Handle unknown circuit gracefully
      const circuit = compressed.c !== 'unknown' ? RACE_CIRCUITS[compressed.c] : null
      
      return {
        position: compressed.p || 0,
        circuit: circuit || {
          id: 'unknown',
          name: 'Unknown Circuit',
          rewardMultiplier: 1,
          xpMultiplier: 1
        },
        raceType: compressed.t || 'unknown',
        finishTime: compressed.f || 0,
        day: compressed.d || 0,
        earnings: compressed.e || 0
      }
    }
  },

  jockey: {
    compressJockey: (jockey) => ({
      i: jockey.id,
      n: jockey.name,
      w: jockey.weight,
      s: jockey.skill, 
      r: jockey.region,
      f: jockey.recentForm
    }),

    decompressJockey: (compressed) => {
      const jockey = new Jockey(compressed.n, compressed.r);
      jockey.id = compressed.i;
      jockey.weight = compressed.w;
      jockey.skill = compressed.s;
      jockey.recentForm = compressed.f || Array(5).fill(null);
      return jockey;
    }
  },

  owner: {
    compressOwner: (owner) => ({
      i: owner.id,
      n: owner.name,
      r: owner.ranchName,
      h: owner.homeRegion,
      m: owner.money,
      c: owner.chocoboIds,
      ...(owner.isPlayer ? 
        { 
          ip: true,
          rep: owner.reputation,
          inv: owner.inventory || []
        } : 
        {
          s: owner.shrewdness,
          a: owner.ambition,
          p: owner.competitiveness
        })
    }),

    decompressOwner: (compressed) => {
      if (compressed.ip) {
        const owner = new PlayerOwner(compressed.n, compressed.r, compressed.h);
        owner.id = compressed.i;
        owner.money = compressed.m;
        owner.chocoboIds = compressed.c || [];
        owner.reputation = compressed.rep || 50;
        owner.inventory = compressed.inv || [];
        return owner;
      } else {
        const owner = new Owner(compressed.n, compressed.r, compressed.h);
        owner.id = compressed.i;
        owner.money = compressed.m;
        owner.chocoboIds = compressed.c || [];
        owner.shrewdness = compressed.s;
        owner.ambition = compressed.a;
        owner.competitiveness = compressed.p;
        return owner;
      }
    }
  },

  chocobo: {
    compressChocobo: (chocobo) => ({
      i: chocobo.id,
      n: chocobo.name,
      c: chocobo.color,
      r: chocobo.region,
      l: chocobo.level,
      x: chocobo.experience,
      g: chocobo.gender,
      b: chocobo.behaviorId,
      bd: chocobo.birthDay || 1,
      ebd: chocobo.estimatedBirthDay,
      // Explicitly store all stat values with rounding
      bs: Math.round(chocobo.baseSpeed),
      bt: Math.round(chocobo.baseStamina),
      sb: Math.round(chocobo.speedBonus),
      tb: Math.round(chocobo.staminaBonus),
      is: Math.round(chocobo.initialSpeedBonus),
      it: Math.round(chocobo.initialStaminaBonus),
      ts: Math.round(chocobo.speed), // Store total speed
      tst: Math.round(chocobo.stamina), // Store total stamina
      xs: chocobo.hasSpeedXFactor,
      xt: chocobo.hasStaminaXFactor,
      tt: chocobo.trainingType,
      ct: chocobo.consecutiveBehaviorTraining,
      pb: chocobo.personalBests,
      rh: (chocobo.raceHistory || [])
        .slice(-STORAGE_OPTIMIZATIONS.raceHistory.maxRacesPerChocobo)
        .map(STORAGE_OPTIMIZATIONS.raceHistory.compressRaceData),
      rf: chocobo.recentForm,
      // Add career summary data if it exists
      cs: chocobo.careerSummary ? {
        ce: chocobo.careerSummary.careerEarnings,
        cw: chocobo.careerSummary.careerWins,
        cr: chocobo.careerSummary.careerRaces,
        fd: chocobo.careerSummary.firstRaceDay,
        ld: chocobo.careerSummary.lastRaceDay
      } : null,
      // Lineage tracking
      mi: chocobo.motherId,
      mn: chocobo.motherName,
      mc: chocobo.motherColor,
      ms: chocobo.motherStats,
      fi: chocobo.fatherId,
      fn: chocobo.fatherName,
      fc: chocobo.fatherColor,
      fs: chocobo.fatherStats,
      // Breeding state
      ib: chocobo.isBreeding,
      br: chocobo.breedingRole,
      bsd: chocobo.breedingStartDay,
      bed: chocobo.breedingEndDay,
      bpi: chocobo.breedingPartnerId,
      bpn: chocobo.breedingPartnerName,
      od: chocobo.offspringData,
      // Sale preferences
      nfs: chocobo.notForSale,
      // Transit state
      it: chocobo.inTransit,
      // Effects data
      ae: chocobo.activeEffects || [],
      eq: chocobo.equipment || null,
      cbi: chocobo.consumedBreedingItems || [],
      pe: chocobo.permanentEnhancements || []
    }),

    decompressChocobo: (compressed) => {
      // Create a plain object first to bypass constructor validation during load
      const chocobo = Object.create(Chocobo.prototype);
      
      // Set all properties directly
      chocobo.id = compressed.i;
      chocobo.name = compressed.n;
      chocobo.color = compressed.c;
      chocobo.region = compressed.r;
      chocobo.level = compressed.l;
      chocobo.experience = compressed.x;
      chocobo.gender = compressed.g;
      chocobo.behaviorId = compressed.b;
      chocobo.birthDay = compressed.bd || 1;
      chocobo.estimatedBirthDay = compressed.ebd || null;
      
      // Restore exact stat values
      chocobo.baseSpeed = compressed.bs;
      chocobo.baseStamina = compressed.bt;
      chocobo.speedBonus = compressed.sb;
      chocobo.staminaBonus = compressed.tb;
      chocobo.initialSpeedBonus = compressed.is;
      chocobo.initialStaminaBonus = compressed.it;
      chocobo.speed = compressed.ts; // Restore total speed
      chocobo.stamina = compressed.tst; // Restore total stamina
      
      chocobo.hasSpeedXFactor = compressed.xs;
      chocobo.hasStaminaXFactor = compressed.xt;
      chocobo.trainingType = compressed.tt;
      chocobo.consecutiveBehaviorTraining = compressed.ct;
      chocobo.personalBests = compressed.pb || {};
      chocobo.raceHistory = compressed.rh ? compressed.rh.map(STORAGE_OPTIMIZATIONS.raceHistory.decompressRaceData) : [];
      chocobo.recentForm = compressed.rf || Array(5).fill(null);
      
      // Restore career summary if it exists
      if (compressed.cs) {
        chocobo.careerSummary = {
          careerEarnings: compressed.cs.ce || 0,
          careerWins: compressed.cs.cw || 0,
          careerRaces: compressed.cs.cr || 0,
          firstRaceDay: compressed.cs.fd || null,
          lastRaceDay: compressed.cs.ld || null
        };
      }
      
      // Restore lineage data
      chocobo.motherId = compressed.mi || null;
      chocobo.motherName = compressed.mn || null;
      chocobo.motherColor = compressed.mc || null;
      chocobo.motherStats = compressed.ms || null;
      chocobo.fatherId = compressed.fi || null;
      chocobo.fatherName = compressed.fn || null;
      chocobo.fatherColor = compressed.fc || null;
      chocobo.fatherStats = compressed.fs || null;
      
      // Restore breeding state
      chocobo.isBreeding = compressed.ib || false;
      chocobo.breedingRole = compressed.br || null;
      chocobo.breedingStartDay = compressed.bsd || null;
      chocobo.breedingEndDay = compressed.bed || null;
      chocobo.breedingPartnerId = compressed.bpi || null;
      chocobo.breedingPartnerName = compressed.bpn || null;
      chocobo.offspringData = compressed.od || null;
      
      // Restore sale preferences
      chocobo.notForSale = compressed.nfs || false;
      
      // Restore transit state
      chocobo.inTransit = compressed.it || false;
      
      // Restore effects data
      chocobo.activeEffects = compressed.ae || [];
      chocobo.equipment = compressed.eq || null;
      chocobo.consumedBreedingItems = compressed.cbi || [];
      chocobo.permanentEnhancements = compressed.pe || [];
      
      // Reconstruct properties that were set in the constructor
      // Set image URL based on color
      chocobo.imageUrl = chocoboImages[chocobo.color] || chocoboImages.yellow;
      
      // Set xFactorPercentage from base stats (with fallback for invalid combinations)
      if (CHOCOBO_BASE_STATS[chocobo.color] && CHOCOBO_BASE_STATS[chocobo.color][chocobo.region]) {
        chocobo.xFactorPercentage = CHOCOBO_BASE_STATS[chocobo.color][chocobo.region].xfactor_percentage || 1;
      } else {
        // Fallback for invalid color/region combinations - use yellow/region1 defaults
        chocobo.xFactorPercentage = 1;
        console.warn(`Invalid color/region combination: ${chocobo.color}/${chocobo.region} for chocobo ${chocobo.name}, using defaults`);
      }
      
      // Verify stats are consistent
      if (chocobo.speed !== chocobo.baseSpeed + chocobo.speedBonus ||
          chocobo.stamina !== chocobo.baseStamina + chocobo.staminaBonus) {
        console.error('Stats mismatch detected on chocobo:', chocobo.name);
        console.error('Expected:', {
          speed: chocobo.baseSpeed + chocobo.speedBonus,
          stamina: chocobo.baseStamina + chocobo.staminaBonus
        });
        console.error('Actual:', {
          speed: chocobo.speed,
          stamina: chocobo.stamina
        });
        // Force recalculation if mismatch detected
        chocobo.updateTotalStats();
      }
      
      return chocobo;
    }
  }
};

export class StorageManager {
  static STORAGE_KEY = 'chocobo-manager-save';

  static compressGameState(gameState) {
    const gameStore = useGameStore();
    try {
      const compressed = {
        ch: gameState.chocobos.map(STORAGE_OPTIMIZATIONS.chocobo.compressChocobo),
        ow: gameState.owners.map(STORAGE_OPTIMIZATIONS.owner.compressOwner),
        jo: gameState.jockeys.map(STORAGE_OPTIMIZATIONS.jockey.compressJockey),
        d: gameState.currentDay,
        au: (gameState.activeAuctions || [])
          .filter(a => a.endDay >= gameState.currentDay)
          .slice(0, 10),
        tr: (gameState.recentTransactions || []).slice(-10),
        rh: (gameState.retirementHistory || []).slice(-20),
        lcd: gameState.lastCleanupDay || 1,
        lpcd: gameState.lastPopulationCheckDay || 1,
        bt: gameState.breedingTransactions || [],
        bin: gameState.breedingIncomeNotifications || [],
        mrc: gameState.monthlyRaceCount || null,
        lrcr: gameState.lastRaceCountReset || 0,
        pr: gameState.performanceRecords || null,
        // Breeding system data
        ab: gameState.activeBreedings || [],
        cb: gameState.completedBreedings || [],
        nt: gameState.notifications || [],
        nbt: gameState.npcBreedingTransactions || [],
        bs: gameState.breedingStats || null,
        bid: gameState.activeBids || []
      };
      
      // Log the compressed size and data integrity
      const serialized = JSON.stringify(compressed);
      const bytes = new TextEncoder().encode(serialized).length;
      const kilobytes = bytes / 1024;
      
      // Saving compressed game state

      // Calculate remaining space
      const testKey = 'storageTest';
      let maxBytes = 0;
      let testData = '';
      const increment = 1024 * 100; // Test in 100KB chunks
      
      try {
        while (true) {
          testData += 'a'.repeat(increment);
          localStorage.setItem(testKey, testData);
          maxBytes = testData.length;
        }
      } catch (e) {
        localStorage.removeItem(testKey);
        const maxMB = maxBytes / 1024 / 1024;
        const remainingMB = (maxBytes - bytes) / 1024 / 1024;
      }

      return compressed;
    } catch (error) {
      console.error('Error compressing game state:', error);
      throw new Error('Failed to compress game state');
    }
  }

  static decompressGameState(compressed) {
    if (!compressed || !compressed.ch || !compressed.ow || !compressed.jo) {
      console.error('Invalid compressed state structure:', compressed);
      return null;
    }

    try {
      return {
        chocobos: compressed.ch.map(STORAGE_OPTIMIZATIONS.chocobo.decompressChocobo),
        owners: compressed.ow.map(STORAGE_OPTIMIZATIONS.owner.decompressOwner),
        jockeys: compressed.jo.map(STORAGE_OPTIMIZATIONS.jockey.decompressJockey),
        currentDay: compressed.d || 1,
        activeAuctions: compressed.au || [],
        recentTransactions: compressed.tr || [],
        retirementHistory: compressed.rh || [],
        lastCleanupDay: compressed.lcd || 1,
        lastPopulationCheckDay: compressed.lpcd || 1,
        breedingTransactions: compressed.bt || [],
        breedingIncomeNotifications: compressed.bin || [],
        monthlyRaceCount: compressed.mrc || null,
        lastRaceCountReset: compressed.lrcr || 0,
        performanceRecords: compressed.pr || null,
        // Breeding system data
        activeBreedings: compressed.ab || [],
        completedBreedings: compressed.cb || [],
        notifications: compressed.nt || [],
        npcBreedingTransactions: compressed.nbt || [],
        breedingStats: compressed.bs || null,
        activeBids: compressed.bid || []
      };
    } catch (error) {
      console.error('Error decompressing game state:', error);
      return null;
    }
  }

  static saveGame(gameState) {
    try {
      const compressed = this.compressGameState(gameState);
      const serialized = JSON.stringify(compressed);
      localStorage.setItem(this.STORAGE_KEY, serialized);
    } catch (error) {
      console.error('Initial save failed, attempting cleanup');
      if (error.name === 'QuotaExceededError') {
        // Storage quota exceeded - attempting optimization
        this.handleStorageLimit(gameState);
      } else {
        throw error;
      }
    }
  }

  static loadGame() {
    try {
      const savedState = localStorage.getItem(this.STORAGE_KEY);
      if (!savedState) return null;
      
      const compressed = JSON.parse(savedState);
      // Loading compressed game state
      
      return this.decompressGameState(compressed);
    } catch (error) {
      console.error('Failed to load game:', error);
      localStorage.removeItem(this.STORAGE_KEY);
      return null;
    }
  }

  static handleStorageLimit(gameState) {
    const optimizations = [
      // Level 1: Reduce recent data
      (state) => ({
        ...state,
        recentTransactions: state.recentTransactions.slice(-5),
        activeAuctions: state.activeAuctions.slice(0, 5)
      }),
      
      // Level 2: Reduce race histories
      (state) => ({
        ...state,
        chocobos: state.chocobos.map(chocobo => ({
          ...chocobo,
          raceHistory: (chocobo.raceHistory || []).slice(-10)
        }))
      }),
      
      // Level 3: Remove non-essential data
      (state) => ({
        ...state,
        chocobos: state.chocobos.map(chocobo => ({
          ...chocobo,
          personalBests: {},
          raceHistory: (chocobo.raceHistory || []).slice(-5)
        }))
      }),
      
      // Level 4: Reduce breeding transactions and performance records
      (state) => ({
        ...state,
        breedingTransactions: (state.breedingTransactions || []).slice(-10),
        breedingIncomeNotifications: (state.breedingIncomeNotifications || []).slice(-5),
        performanceRecords: state.performanceRecords ? {
          monthlyRecords: {
            short: (state.performanceRecords.monthlyRecords?.short || []).slice(0, 5),
            medium: (state.performanceRecords.monthlyRecords?.medium || []).slice(0, 5),
            long: (state.performanceRecords.monthlyRecords?.long || []).slice(0, 5)
          },
          allTimeRecords: {
            short: (state.performanceRecords.allTimeRecords?.short || []).slice(0, 5),
            medium: (state.performanceRecords.allTimeRecords?.medium || []).slice(0, 5),
            long: (state.performanceRecords.allTimeRecords?.long || []).slice(0, 5)
          }
        } : null
      })
    ];

    let optimizedState = gameState;
    
    // Try each optimization level
    for (const optimize of optimizations) {
      try {
        optimizedState = optimize(optimizedState);
        const compressed = this.compressGameState(optimizedState);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(compressed));
        return true;
      } catch (error) {
        if (error.name !== 'QuotaExceededError') throw error;
        continue;
      }
    }

    throw new Error('Storage limit reached after all optimizations');
  }
}
export { STORAGE_OPTIMIZATIONS };